import axios from "axios";
import router from "src/main.js";

const API_ENDPOINT = process.env.VUE_APP_API_ENDPOINT;

const authHeader = () => ({
  Authorization: `Bearer ${localStorage.getItem("token")}`,
});

const client = axios.create({
  baseURL: API_ENDPOINT,
  headers: {
    Authorization: `Bearer ${localStorage.getItem("token")}`,
    "Content-Type": "application/json",
  },
});

class DataService {
  static get(path = "") {
    return client({
      method: "GET",
      url: path,
      headers: { ...authHeader() },
    });
  }

  static post(path = "", data = {}, optionalHeader = {}) {
    return client({
      method: "POST",
      url: path,
      data,
      headers: { ...authHeader(), ...optionalHeader },
    });
  }

  static patch(path = "", data = {}, optionalHeader = {}) {
    return client({
      method: "PATCH",
      url: path,
      data,
      headers: { ...authHeader(), ...optionalHeader },
    });
  }

  static delete(path = "", data = {}) {
    return client({
      method: "DELETE",
      url: path,
      data: JSON.stringify(data),
      headers: { ...authHeader() },
    });
  }

  static put(path = "", data = {}, optionalHeader = {}) {
    return client({
      method: "PUT",
      url: path,
      data: JSON.stringify(data),
      headers: { ...authHeader(), ...optionalHeader },
    });
  }
}

let isRefreshing = false;
let failedQueue = [];
let timeStampRefresh = null;
let token = null;

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

const delayRequest = async (originalRequest) => {
  return new Promise((resolve, reject) => {
    failedQueue.push({ resolve, reject });
  })
    .then((token) => {
      originalRequest.headers.Authorization = `Bearer ${token}`;
      return client(originalRequest);
    })
    .catch((err) => {
      return Promise.reject(err);
    });
};

client.interceptors.request.use(
  async (config) => {
    config.timeStamp = new Date().getTime();
    if (isRefreshing && !config.url.includes("/auth/refresh")) {
      return delayRequest(config);
    } else {
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    }
  },
  (error) => {
    return Promise.reject(error);
  }
);

client.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    if (
      error.response &&
      error.response.status === 401 &&
      !originalRequest._retry
    ) {
      originalRequest._retry = true;
      token = null;
      if (error.config.timeStamp < timeStampRefresh) {
        originalRequest.headers.Authorization = `Bearer ${token}`;
        return client(originalRequest);
      }
      if (isRefreshing) {
        try {
          const newToken = await delayRequest(originalRequest);
          return newToken;
        } catch (err) {
          return Promise.reject(err);
        }
      } else {
        isRefreshing = true;
        timeStampRefresh = new Date().getTime();
        const refreshToken = localStorage.getItem("refreshToken");

        if (refreshToken) {
          try {
            const response = await axios.get(`${API_ENDPOINT}auth/refresh`, {
              headers: {
                Authorization: `Bearer ${refreshToken}`,
              },
            });
            if (response && response.data && response.data.token) {
              token = response.data.token;
              client.defaults.headers.common.Authorization = `Bearer ${response.data.token}`;
              localStorage.setItem("token", response.data.token);
              localStorage.setItem("refreshToken", response.data.refreshToken);
              originalRequest.headers.Authorization = `Bearer ${response.data.token}`;
              isRefreshing = false;
              processQueue(null, response.data.token);
              return client(originalRequest);
            }
          } catch (err) {
            isRefreshing = false;
            processQueue(err, null);
            localStorage.removeItem("token");
            localStorage.removeItem("refreshToken");
            localStorage.removeItem("user");
            router.push("/");
            return Promise.reject(err);
          }
        } else {
          isRefreshing = false;
          localStorage.removeItem("token");
          localStorage.removeItem("refreshToken");
          localStorage.removeItem("user");
          router.push("/");
          return Promise.reject(error);
        }
      }
    }
    return Promise.reject(error);
  }
);

export { DataService };
